// Central tour configuration and utilities
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

export interface TourConfig {
    tourId: string;
    steps: DriveStep[];
    onComplete?: () => void;
    initialStep?: number;
}

const TOUR_PROGRESS_PREFIX = 'tour_progress_';

// Reusable HTML for "Continue Platform Tour" button
// Add this to the last step of any tour that can be accessed from the Landing Tour
export const CONTINUE_PLATFORM_TOUR_BUTTON = `
    <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.1);">
        <button 
            class="driver-return-platform-tour-btn"
            style="
                background: linear-gradient(to right, #b8860b, #daa520);
                border: none;
                color: #000;
                font-weight: bold;
                padding: 8px 16px;
                border-radius: 6px;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                gap: 8px;
                font-size: 0.9rem;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.5);
                transition: all 0.2s ease;
            "
            onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 8px -1px rgba(0,0,0,0.6)'"
            onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 6px -1px rgba(0,0,0,0.5)'"
        >
            <span>🔙</span> Continue Platform Tour
        </button>
    </div>
`;

// Save progress
export const saveTourProgress = (tourId: string, currentStepIndex: number) => {
    localStorage.setItem(`${TOUR_PROGRESS_PREFIX}${tourId}`, currentStepIndex.toString());
};

// Get progress
export const getTourProgress = (tourId: string): number | null => {
    const saved = localStorage.getItem(`${TOUR_PROGRESS_PREFIX}${tourId}`);
    return saved ? parseInt(saved, 10) : null;
};

// Clear progress (when finished)
export const clearTourProgress = (tourId: string) => {
    localStorage.removeItem(`${TOUR_PROGRESS_PREFIX}${tourId}`);
};

// Declare global function
declare global {
    interface Window {
        __techUnityStartDeepDive: (url: string, title: string) => void;
        __currentTourInstance?: any; // Track the active tour instance
    }
}

// Global handler for Deep Dive
window.__techUnityStartDeepDive = (url: string, title: string) => {
    console.log(`🚀 Starting Deep Dive to: ${title} (${url})`);

    // CRITICAL: Destroy the current tour before navigating
    if (window.__currentTourInstance) {
        console.log('🛑 Destroying current tour before navigation');
        try {
            window.__currentTourInstance.destroy();
        } catch (e) {
            console.warn('Failed to destroy tour:', e);
        }
        window.__currentTourInstance = null;
    }

    // Save state
    sessionStorage.setItem('isDeepDiving', 'true');
    sessionStorage.setItem('deepDiveReturnUrl', window.location.hash || '/');

    console.log('📝 SessionStorage set:', {
        isDeepDiving: sessionStorage.getItem('isDeepDiving'),
        deepDiveReturnUrl: sessionStorage.getItem('deepDiveReturnUrl')
    });

    // Redirect with autoStart param - use hash-based routing
    // Since we're using HashRouter, we need to add the # prefix
    window.location.href = `#${url}?autoStartTour=true`;
};

// Global Event Delegation for Tour Navigation Buttons
// We attach this ONCE to the document to handle all tour interactions robustly
if (typeof document !== 'undefined') {
    document.addEventListener('click', (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // 1. Deep Dive button (Landing -> Feature page)
        const deepDiveBtn = target.closest('.driver-deep-dive-btn');
        if (deepDiveBtn) {
            e.preventDefault();
            e.stopPropagation();

            const url = deepDiveBtn.getAttribute('data-deep-dive-url');
            const title = deepDiveBtn.getAttribute('data-deep-dive-title');

            if (url && title) {
                console.log(`🚀 Starting Deep Dive to: ${title} (${url})`);
                window.__techUnityStartDeepDive(url, title);
            }
            return;
        }

        // 2. Return to Platform Tour button (Feature page -> Landing)
        const returnBtn = target.closest('.driver-return-platform-tour-btn');
        if (returnBtn) {
            e.preventDefault();
            e.stopPropagation();

            console.log('🔙 Returning to Platform Tour');

            // Destroy the current tour instance to remove overlay
            if (window.__currentTourInstance) {
                try {
                    window.__currentTourInstance.destroy();
                } catch (e) {
                    console.warn('Failed to destroy tour on return:', e);
                }
                window.__currentTourInstance = null;
            }

            sessionStorage.removeItem('isDeepDiving');
            // Use window.location.assign to force navigation event if needed, 
            // but setting hash is usually enough for HashRouter.
            // We use a timestamp to ensure the hash changes even if already there (edge case)
            window.location.href = '#/?autoStartTour=true&t=' + Date.now();
            return;
        }
    });
}

// Create a tour driver instance with consistent styling
export const createTour = (config: TourConfig) => {
    const { tourId, steps, onComplete } = config;

    // Shared state to track the active original step object
    // This solves the issue of losing custom properties like 'deepDive'
    let currentActiveStep: any = null;

    const driverObj = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        doneBtnText: 'Done',
        nextBtnText: 'Next →',
        prevBtnText: '← Previous',
        steps,
        onDeselected: () => {
            // Clear any saved progress when user manually closes
            // clearTourProgress(tourId);
        },

        onHighlightStarted: (_element: Element | undefined, step: DriveStep | undefined, _options: unknown) => {
            if (step && step.popover) {
                // Find the original step configuration from our source array
                // We use multiple matching strategies to be robust
                const matchedStep = steps.find(s => {
                    // Strategy 1: Title & Description match (most reliable for content identification)
                    if (s.popover?.title === step.popover?.title &&
                        s.popover?.description === step.popover?.description) {
                        return true;
                    }
                    // Strategy 2: Element ID match
                    if (typeof step.element === 'string' && typeof s.element === 'string') {
                        return step.element === s.element;
                    }
                    if (step.element instanceof Element && typeof s.element === 'string') {
                        const originalId = s.element.startsWith('#') ? s.element.slice(1) : s.element;
                        return step.element.id === originalId;
                    }
                    return false;
                });

                if (matchedStep) {
                    currentActiveStep = matchedStep;
                    // Also save progress
                    const index = steps.indexOf(matchedStep);
                    if (index !== -1) {
                        saveTourProgress(tourId, index);
                    }
                }
            }
        },
        onPopoverRender: (popover: any, _step: any) => {
            // Use requestAnimationFrame to ensure the DOM update from driver.js is finishing
            requestAnimationFrame(() => {
                const footer = popover.wrapper.querySelector('.driver-popover-footer');

                // 1. Deep Dive Button Handler (Direct attachment)
                // We attach directly here because driver.js might stop propagation, killing our global listener.
                const deepDiveBtn = popover.wrapper.querySelector('.driver-deep-dive-btn');
                if (deepDiveBtn) {
                    // Clone to strip existing listeners if any, or just add new one (safer to just add)
                    // We use 'onclick' property to ensure single handler and overwrite any duds
                    deepDiveBtn.onclick = (e: any) => {
                        e.preventDefault();
                        e.stopPropagation(); // We handle it here
                        const url = deepDiveBtn.getAttribute('data-deep-dive-url');
                        const title = deepDiveBtn.getAttribute('data-deep-dive-title');

                        console.log('🔘 Deep Dive Button Clicked (Direct Handler)', { url, title });

                        if (url && title) {
                            window.__techUnityStartDeepDive(url, title);
                        }
                    };
                }

                if (!footer) return;

                // 2. Return to Main Tour Button (Feature -> Landing)
                const isDeepDiving = sessionStorage.getItem('isDeepDiving') === 'true';

                // Re-calculate if this is the last step using the matched original step
                const currentStepIndex = currentActiveStep ? steps.indexOf(currentActiveStep) : -1;
                const isLastStep = currentStepIndex !== -1 && currentStepIndex === steps.length - 1;

                console.log('🔍 Deep Dive Check:', {
                    isDeepDiving,
                    isLastStep,
                    currentStepIndex,
                    totalSteps: steps.length,
                    tourId
                });

                if (isDeepDiving && isLastStep) {
                    console.log('✅ Showing "Continue Platform Tour" button');
                    if (footer.querySelector('.driver-return-tour-btn')) return;

                    const returnBtn = document.createElement('button');
                    returnBtn.className = 'driver-return-tour-btn driver-popover-done-btn';
                    returnBtn.innerHTML = '<span>🔙</span> Continue Platform Tour';
                    returnBtn.style.cssText = `
                        margin-left: 10px !important;
                        background: linear-gradient(to right, #b8860b, #daa520) !important;
                        color: #000 !important;
                        font-weight: bold !important;
                        border: none !important;
                        padding: 6px 14px !important;
                        border-radius: 4px !important;
                        cursor: pointer !important;
                        display: inline-flex !important;
                        align-items: center !important;
                        gap: 6px !important;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
                     `;

                    returnBtn.onclick = (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        console.log('🔙 Returning to landing page tour');
                        sessionStorage.removeItem('isDeepDiving');
                        // Return to landing page with autoStartTour param to trigger seamless resume
                        // Use hash-based routing for HashRouter compatibility
                        window.location.href = '#/?autoStartTour=true';
                    };

                    footer.appendChild(returnBtn);

                    // Hide default "Done" button
                    const doneBtn = footer.querySelector('.driver-popover-done-btn:not(.driver-return-tour-btn)');
                    if (doneBtn) {
                        (doneBtn as HTMLElement).style.display = 'none';
                        console.log('✅ Hidden default Done button');
                    } else {
                        console.warn('⚠️ Could not find default Done button to hide');
                    }
                } else {
                    console.log('❌ Not showing return button - conditions not met');
                }
            });
        },
        onDestroyed: () => {
            // CRITICAL FIX: Only clear progress if we actually COMPLETED the tour.
            // If the user refreshes the page or closes manually, we want to KEEP progress 
            // so they can resume (handled by WelcomeModal or startTour checks).

            // We can't easily check driverObj.getActiveIndex() here because it might be destroyed already.
            // But we can check our own localStorage progress.
            const savedProgress = getTourProgress(tourId);
            const totalSteps = steps.length;

            if (savedProgress !== null && savedProgress >= totalSteps - 1) {
                // Tour was finished (or at least validly at the end)
                localStorage.setItem(`hasSeenTour_${tourId}`, 'true');
                clearTourProgress(tourId);
                if (onComplete) {
                    onComplete();
                }
            }
        },
    });

    return driverObj;
};

// Start a tour if not seen before
export const startTourIfNew = (config: TourConfig, delay: number = 1500) => {
    const hasSeenTour = localStorage.getItem(`hasSeenTour_${config.tourId}`);

    if (!hasSeenTour) {
        setTimeout(() => {
            startTour(config);
        }, delay);
    }
};

// Manually start a tour (ignore localStorage)
export const startTour = (config: TourConfig) => {
    // Safety: Destroy any existing tour instance to prevent overlay conflicts
    if (window.__currentTourInstance) {
        try {
            console.log('🛑 Cleanup: Destroying previous tour instance before starting new one');
            window.__currentTourInstance.destroy();
        } catch (e) {
            console.warn('⚠️ Failed to destroy previous tour:', e);
        }
        window.__currentTourInstance = undefined;
    }

    const tour = createTour(config);

    // Store globally so we can destroy it before deep dive navigation
    window.__currentTourInstance = tour;

    // Determine start step: explicit initialStep -> saved progress -> 0
    let startStep = config.initialStep ?? 0;

    // If no explicit start step provided, check if we have saved progress
    if (config.initialStep === undefined) {
        const saved = getTourProgress(config.tourId);
        if (saved !== null) {
            startStep = saved;
        }
    }

    tour.drive(startStep);
};

// Reset tour progress (for testing or user request)
export const resetTour = (tourId: string) => {
    localStorage.removeItem(`hasSeenTour_${tourId}`);
    clearTourProgress(tourId);
};

// Reset all tours
export const resetAllTours = () => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
        if (key.startsWith('hasSeenTour_') || key.startsWith(TOUR_PROGRESS_PREFIX)) {
            localStorage.removeItem(key);
        }
    });
};

// Check if tour has been seen
export const hasTourBeenSeen = (tourId: string): boolean => {
    return localStorage.getItem(`hasSeenTour_${tourId}`) === 'true';
};
